import { calcCSS, calcFGTLS, calculAF, PLAFOND_CNAMGS, TAUX_CNSS } from '@shared/constants/Taux';
import { montantCotisation, roundFCFA } from '@shared/utils/round';
import { defaultParametresFiscauxPaie, type ParametresFiscauxPaie } from './ParametresFiscauxPaie';
import { ventilerCnssPatronale } from './VentilationCnss';

/** @see `calculAF` — ventilation AF (incluses dans CNSS patronale, non additives). */
export { calculAF };

/** FGTLS patronal 1 % (Loi 3/94) — source `Taux.ts` ; alias `TAUX_FGTLS` = `FGTLS_TAUX`. */
export { calcFGTLS, FGTLS_TAUX, TAUX_FGTLS } from '@shared/constants/Taux';

/**
 * Plafond mensuel CNSS (= `TAUX_CNSS.PLAFOND_MENSUEL` dans `Taux.ts`).
 * Assiette CNSS = Math.min(brut, PLAFOND_MENSUEL).
 */
export const PLAFOND_MENSUEL = TAUX_CNSS.PLAFOND_MENSUEL;

/** Plafond mensuel CNAMGS (= `PLAFOND_CNAMGS` / 2 500 000). */
export const PLAFOND_MENSUEL_CNAMGS = PLAFOND_CNAMGS;

export interface CotisationsInput {
  /** brut_total = cat1 + cat2 */
  brutTotal: number;
  /** Indemnités cat2 exclues de la base CNAMGS / TCS / IRPP */
  cat2?: number;
  /**
   * Primes exonérées par l'art. 91 11° (et art. 347 pour la TCS), déjà plafonnées par le cumul annuel de
   * 4 000 000 FCFA : retirées de la base TCS. Les cotisations CNSS / CNAMGS restent calculées sur le brut entier.
   */
  exoArt9111?: number;
}

export interface CotisationsResult {
  base_cnss: number;
  base_cnamgs: number;
  /** Base TCS = brut − cat2 − exonération art. 91 11° − CNSS sal − CNAMGS sal (avant seuil d'exonération) */
  base_tcs: number;
  cnss_pf_emp: number;
  cnss_rp_emp: number;
  cnss_vid_emp: number;
  cnss_vid_sal: number;
  cnamgs_emp: number;
  cnamgs_fam_emp: number;
  cnamgs_sal: number;
  cfp_emp: number;
  fnh_emp: number;
  /**
   * FGTLS patronale — 1 % du brut, sans plafond (Loi 3/94).
   * Incluse dans `total_charges_emp`.
   */
  fgtls_emp: number;
  /**
   * Ventilation Allocations Familiales (8 % plaf. 1,5 M) — **incluse** dans `cnss_pf_emp`,
   * pas ajoutée à `total_charges_emp` (CNSS patronale = 18 % agrégé).
   */
  af_emp: number;
  /** Retenue salariale TCS — hors charges patronales. */
  tcs: number;
  /** CSS — Contribution Spéciale de Solidarité (1 % brut si > 480 000). */
  css: number;
  total_charges_emp: number;
}

function normalizeInput(brutOrInput: number | CotisationsInput): CotisationsInput {
  if (typeof brutOrInput === 'number') {
    return { brutTotal: brutOrInput, cat2: 0 };
  }
  return {
    brutTotal: brutOrInput.brutTotal,
    cat2: brutOrInput.cat2 ?? 0,
    exoArt9111: brutOrInput.exoArt9111 ?? 0,
  };
}

/**
 * Cotisations sociales + TCS (salariale) + TFP/CDS (patronales).
 *
 * Source unique : `src/shared/constants/Taux.ts`
 * (`TAUX_CNSS` / `TAUX_CNAMGS`) via `ParametresFiscauxPaie` (injectable tenant).
 *
 * Assiettes :
 * - CNSS   : Math.min(brut, PLAFOND_MENSUEL)     // TAUX_CNSS.PLAFOND_MENSUEL
 * - CNAMGS : Math.min(brut − cat2, plafondCnamgs) // TAUX_CNAMGS.PLAFOND_MENSUEL = 2,5 M
 *
 * Ne pas hardcoder 0.05 / 0.18 / 0.02 / 0.041 — lire `TAUX_CNSS` / `TAUX_CNAMGS`.
 */
export function calculerCotisations(
  brutOrInput: number | CotisationsInput,
  params: ParametresFiscauxPaie = defaultParametresFiscauxPaie(),
): CotisationsResult {
  const { brutTotal, cat2 = 0, exoArt9111 = 0 } = normalizeInput(brutOrInput);
  const brut = Math.max(0, brutTotal);
  const indemExcl = Math.max(0, Math.min(cat2, brut));

  /** Assiette CNSS = Math.min(brut, PLAFOND_MENSUEL). */
  const base_cnss = Math.min(brut, params.plafondCnss);

  /** Assiette CNAMGS = Math.min(brut − cat2, plafond 2,5 M). */
  const base_cnamgs = Math.min(Math.max(0, brut - indemExcl), params.plafondCnamgs);

  /**
   * CNSS patronale ventilée (communiqué CNSS du 12/01/2026, décret 0487/PR/MASI) : prestations familiales 5 %
   * (`cnss_pf_emp`) + AT-MP 2 % (`cnss_rp_emp`) + VID 11 % (`cnss_vid_emp`) = 18 %, sur la même base plafonnée.
   * Le total est calculé sur le taux global et arrondi une seule fois : seule la répartition est exposée.
   */
  const ventilation = ventilerCnssPatronale(base_cnss, {
    pf: params.tauxCnssPfEmp,
    atmp: params.tauxCnssRpEmp,
    vid: params.tauxCnssVidEmp,
  });
  const cnss_pf_emp = ventilation.pf;
  const cnss_rp_emp = ventilation.atmp;
  const cnss_vid_emp = ventilation.vid;
  const cnss_vid_sal = montantCotisation(base_cnss, params.tauxCnssSal);

  /** CNAMGS salariale + patronale (défauts = TAUX_CNAMGS.SALARIAL / .PATRONAL = 2 % / 4,1 %). */
  const cnamgs_emp = montantCotisation(base_cnamgs, params.tauxCnamgsEmp);
  const cnamgs_fam_emp = montantCotisation(base_cnamgs, params.tauxCnamgsFamEmp);
  const cnamgs_sal = montantCotisation(base_cnamgs, params.tauxCnamgsSal);

  /** Sophie FH / TBL : CFP et FNH sur la même base que la CNSS (plafond mensuel). */
  const cfp_emp = montantCotisation(base_cnss, params.tauxTfp);
  const fnh_emp = montantCotisation(base_cnss, params.tauxCds);

  /** AF 8 % = part PF dans la CNSS 18 % (plafonnée) — plafonnée aussi par cnss_pf_emp. */
  const af_emp = Math.min(calculAF(brut), cnss_pf_emp);

  /** Base TCS (référentiel) = brut hors CNSS-seule − primes exonérées art. 91 11° − retenues CNSS et CNAMGS, avant seuil. */
  const base_tcs = Math.max(0, brut - indemExcl - Math.max(0, exoArt9111) - cnss_vid_sal - cnamgs_sal);
  const tcs = roundFCFA(Math.max(0, (base_tcs - params.tcsSeuil) * params.tauxTcs));
  /** CSS après TCS : 1 % du brut si brut > seuil 3×SMIG 2026. */
  const css = calcCSS(brut, params.tauxCss, params.seuilCss);

  /** FGTLS 1 % patronal sur brut (sans plafond), distinct de la CSS salariale. */
  const fgtls_emp = calcFGTLS(brut, params.tauxFgtls);

  const total_charges_emp =
    cnss_pf_emp +
    cnss_rp_emp +
    cnss_vid_emp +
    cnamgs_emp +
    cnamgs_fam_emp +
    cfp_emp +
    fnh_emp +
    fgtls_emp;

  return {
    base_cnss,
    base_cnamgs,
    base_tcs,
    cnss_pf_emp,
    cnss_rp_emp,
    cnss_vid_emp,
    cnss_vid_sal,
    cnamgs_emp,
    cnamgs_fam_emp,
    cnamgs_sal,
    cfp_emp,
    fnh_emp,
    fgtls_emp,
    af_emp,
    tcs,
    css,
    total_charges_emp,
  };
}
